import {
  closeOpsDay,
  createAdminAircraft,
  createNewGame,
  createPlayerRotation,
  createCustomFlight,
  deleteAdminAircraft,
  deletePlayerRotation as deleteRemotePlayerRotation,
  redispatchFlight,
  startOpsDay,
  tickOpsDay,
  updateAdminAircraft,
  updatePlayerRotation
} from "./api";
import { type AircraftInput } from "../domain/simulation";
import { normalizeTime } from "../domain/rules/schedule";
import type { AirportConfigSet, CustomFlightKind, GlobalEvent, OpsDayCloseResult, RedispatchAction, SaveGame, ScheduleSource, WorldData } from "../domain/types";


export interface CustomFlightDraft {
  kind: CustomFlightKind;
  origin: string;
  destination: string;
  aircraftTypeId: string;
  scheduledOffsetDays: number;
  departureTimeLocal: string;
  recoveryForFlightInstanceId: string;
}

export interface RotationDraft {
  id?: string;
  name: string;
  occurrenceIds: string[];
  scheduledFlightIds: string[];
}

export async function createSessionGame(airlineName: string): Promise<{ save: SaveGame; world?: WorldData; airportConfigs?: AirportConfigSet; schedule?: ScheduleSource }> {
  return createNewGame(airlineName);
}

export async function startSessionOpsDay(): Promise<{ save: SaveGame; world?: WorldData; schedule?: ScheduleSource; activeEvents?: GlobalEvent[] }> {
  return startOpsDay();
}

export async function tickSessionOpsDay(minutes: number): Promise<{ save: SaveGame; world?: WorldData; schedule?: ScheduleSource; activeEvents?: GlobalEvent[] }> {
  return tickOpsDay(minutes);
}

export async function redispatchSessionFlight(action: RedispatchAction): Promise<{ save: SaveGame; world?: WorldData; schedule?: ScheduleSource; activeEvents?: GlobalEvent[] }> {
  return redispatchFlight(action);
}

export async function closeSessionOpsDay(): Promise<OpsDayCloseResult & { world?: WorldData; schedule?: ScheduleSource }> {
  return closeOpsDay();
}

export async function createSessionCustomFlight(save: SaveGame, draft: CustomFlightDraft): Promise<{ save: SaveGame; title: string }> {
  const scheduledDay = save.day + Math.max(0, draft.scheduledOffsetDays);
  const title = customFlightTitle(draft);
  const result = await createCustomFlight({
    kind: draft.kind,
    title,
    origin: draft.origin,
    destination: draft.destination,
    scheduledDay,
    departureTimeLocal: normalizeTime(draft.departureTimeLocal),
    aircraftTypeId: draft.aircraftTypeId,
    recoveryForFlightInstanceId: draft.recoveryForFlightInstanceId || undefined
  });
  return { ...result, title };
}

function customFlightTitle(draft: CustomFlightDraft): string {
  return `${customFlightKindLabel(draft.kind)} ${draft.origin}-${draft.destination}`;
}

function customFlightKindLabel(kind: CustomFlightKind): string {
  if (kind === "positioning") {
    return "空机调机";
  }
  return "恢复航班";
}

export async function createSessionPlayerRotation(draft: RotationDraft): Promise<{ save: SaveGame; name: string }> {
  const payload = {
    name: draft.name,
    occurrenceIds: draft.occurrenceIds,
    scheduledFlightIds: draft.scheduledFlightIds
  };
  let result: { save: SaveGame };
  if (draft.id) {
    result = await updatePlayerRotation(draft.id, payload);
  } else {
    result = await createPlayerRotation(payload);
  }
  let rotation: SaveGame["rotations"][number] | undefined = result.save.rotations[result.save.rotations.length - 1];
  if (draft.id) {
    rotation = result.save.rotations.find((item) => item.id === draft.id);
  }
  return { ...result, name: rotation?.name ?? draft.name };
}

export async function deleteSessionPlayerRotation(save: SaveGame, rotationId: string): Promise<{ save: SaveGame; name: string }> {
  const rotation = save.rotations.find((item) => item.id === rotationId);
  const result = await deleteRemotePlayerRotation(rotationId);
  return { ...result, name: rotation?.name ?? rotationId };
}

export async function upsertSessionAircraft(input: AircraftInput): Promise<{ save: SaveGame; registration: string }> {
  const { id, ...body } = input;
  let result: { save: SaveGame };
  if (id) {
    result = await updateAdminAircraft(id, body);
  } else {
    result = await createAdminAircraft(body);
  }
  let aircraft = result.save.fleet.find((item) => item.registration === input.registration.trim().toUpperCase());
  if (id) {
    aircraft = result.save.fleet.find((item) => item.id === id);
  }
  return { ...result, registration: aircraft?.registration ?? input.registration };
}

export async function deleteSessionAircraft(save: SaveGame, aircraftId: string): Promise<{ save: SaveGame; registration: string }> {
  const registration = save.fleet.find((item) => item.id === aircraftId)?.registration ?? aircraftId;
  const result = await deleteAdminAircraft(aircraftId);
  return { ...result, registration };
}
